/**
 * List pages для Block 2.2 (2026-05-26).
 * Editor + modal — TECHDEBT (окрема міні-порція).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  sickLeave,
  sickLeaveCalculation,
  type SickLeave,
  type SickLeaveCalculation,
} from '@/api/sick_leave';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import type { ColDef } from 'ag-grid-community';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';


const fmt2 = (v: string | number | null) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


const SICK_LEAVE_TYPE_LABELS: Record<string, string> = {
  GENERAL_ILLNESS: 'Загальне захворювання',
  CHILD_CARE: 'Догляд за дитиною',
  MATERNITY: 'Вагітність і пологи',
  WORK_ACCIDENT: 'Виробничий нещасний випадок',
};


export function SickLeavesPage() {
  const navigate = useNavigate();
  // 2026-08-23 (Норма #29): серверна пагінація/сортування, ПІБ з сервера,
  // фільтр орг рядком, колонка «Підпис» (sign-control doc.sick_leaves).
  const [orgId, setOrgId] = useOrgFilter();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])), [orgList.data]);
  const { datasource, total } = useInfiniteGridDatasource<SickLeave>(
    (limit, offset, sort) => sickLeave.list({
      organization_id: orgId || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId],
    { docType: 'doc.sick_leaves' },
  );
  const sc = useSignControlInfinite<SickLeave>('doc.sick_leaves', orgId);
  const cols = useMemo<ColDef<SickLeave>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: SickLeave }) => <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 130 },
    {
      field: 'document_date', headerName: 'Дата', width: 120,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 180,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    { field: 'employee_name', headerName: 'Працівник', flex: 1, minWidth: 200,
      valueFormatter: (p) => p.data ? (p.value ?? '—') : '' },
    {
      field: 'sick_leave_type', headerName: 'Тип', width: 200,
      valueFormatter: (p) => p.data ? (SICK_LEAVE_TYPE_LABELS[p.value] ?? p.value) : '',
    },
    { field: 'start_date', headerName: 'З', width: 110 },
    { field: 'end_date', headerName: 'По', width: 110 },
    { field: 'days_count', headerName: 'Днів', width: 80, cellStyle: { textAlign: 'right' } },
    { field: 'certificate_number', headerName: 'Довідка', width: 120 },
    {
      field: 'insurance_stage_years', headerName: 'Стаж', width: 90, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.data ? (p.value ? `${p.value} р.` : '') : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 110, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [orgName]);
  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Лікарняні<TitleTotal total={total} /></h2>
        <button className="primary" onClick={() => navigate('/hr/sick-leaves/new')}>
          + Новий лікарняний
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', margin: '0 0 0.6rem' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
      </div>
      <InfiniteDocGrid<SickLeave>
        deleteRow={{ remove: (id) => sickLeave.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="sick-leaves"
        onRowDoubleClicked={(r) => navigate(`/hr/sick-leaves/${r.id}`)}
        getRowStyle={sc.getRowStyle}
      />
    </div>
  );
}


export function SickLeaveCalculationsPage() {
  const list = useQuery({
    queryKey: ['sick-leave-calcs'],
    queryFn: () => sickLeaveCalculation.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<SickLeaveCalculation>[]>(
    () => [
      { key: 'document_number', header: '№', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 130,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleDateString('uk-UA') : '',
      },
      { key: 'employee_id', header: 'Працівник', width: 220, sortValue: (r) => r.employee_id ?? '', cell: (r) => r.employee_id },
      {
        key: 'sick_leave_type', header: 'Тип', width: 200,
        sortValue: (r) => SICK_LEAVE_TYPE_LABELS[r.sick_leave_type] ?? r.sick_leave_type,
        cell: (r) => SICK_LEAVE_TYPE_LABELS[r.sick_leave_type] ?? r.sick_leave_type,
      },
      {
        key: 'days_count', header: 'Днів', width: 80, align: 'right',
        sortValue: (r) => r.days_count, cell: (r) => r.days_count,
      },
      {
        key: 'employer_amount', header: 'Роботодавець', width: 130, align: 'right',
        sortValue: (r) => Number(r.employer_amount), cell: (r) => fmt2(r.employer_amount),
      },
      {
        key: 'pfu_amount', header: 'ПФУ/Фонд', width: 130, align: 'right',
        sortValue: (r) => Number(r.pfu_amount), cell: (r) => fmt2(r.pfu_amount),
      },
      {
        key: 'total_gross_amount', header: 'Нараховано', width: 140, align: 'right',
        sortValue: (r) => Number(r.total_gross_amount), cell: (r) => fmt2(r.total_gross_amount),
      },
      {
        key: 'net_amount', header: 'До виплати', width: 140, align: 'right',
        sortValue: (r) => Number(r.net_amount), cell: (r) => fmt2(r.net_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0, cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Розрахунки лікарняних</h2>
      </div>
      <DataTable<SickLeaveCalculation>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
